import React, { useMemo } from "react";
import { Card } from "react-bootstrap";
import { useCartStore } from "../../../store/cart-store";

interface OrderSummaryProps {
  selectedPaymentMethod?: string | null;
  shippingCost: number;
  deliveryType: "DELIVERY" | "STORE_PICKUP" | undefined;
}

const OrderSummary: React.FC<OrderSummaryProps> = ({
  selectedPaymentMethod = "",
  shippingCost = 0,
  deliveryType,
}) => {
  const { getTotalPrice, items } = useCartStore();
  const totalPrice: number = getTotalPrice() as number;

  // Obtener la moneda del primer producto (asumimos que todos los productos tienen la misma moneda)
  const currency =
    items.length > 0 && items[0].currency ? items[0].currency : "COP";

  // Calcular el total de puntos de fidelidad y bono influencer
  const { totalLoyaltyPoints } = items.reduce(
    (acc, item) => {
      if (item.aditionalData && item.aditionalData.length > 0) {
        const { loyalty_points = 0 } = item.aditionalData[0];
        return {
          totalLoyaltyPoints:
            acc.totalLoyaltyPoints + loyalty_points * (item.quantity || 1),
        };
      }
      return acc;
    },
    { totalLoyaltyPoints: 0 },
  );

  // Obtener el precio en moneda local desde los productos
  const totalPriceLocal = items.reduce((total, item) => {
    if (item.aditionalData && item.aditionalData.length > 0) {
      return (
        total +
        item.aditionalData[0].price_local_currency * (item.quantity || 1)
      );
    }
    return total;
  }, 0);

  // Determinar si mostrar USDT como moneda principal (para CoinPayment)
  const showUsdtAsPrimary = selectedPaymentMethod === "coinpayment";

  const calculateCommission = (
    amount: number,
    selectedPaymentMethod: string,
  ) => {
    if (
      selectedPaymentMethod === "pse_payment" ||
      selectedPaymentMethod === "nequi_payment"
    ) {
      return { percentage: 0, value: 0 };
    }

    if (
      selectedPaymentMethod === "bank_transfer" ||
      selectedPaymentMethod === "cash_efecty"
    ) {
      if (amount <= 299999) {
        return { percentage: 2.4, value: amount * 0.024 };
      }

      if (amount >= 300000 && amount <= 549999) {
        return { percentage: 2.2, value: amount * 0.022 };
      }

      if (amount >= 550000 && amount <= 999999) {
        return { percentage: 1.9, value: amount * 0.019 };
      }

      if (amount >= 1000000) {
        return { percentage: 1.8, value: amount * 0.018 };
      }
    }

    if (selectedPaymentMethod === "card_payment") {
      return { percentage: 3.5, value: amount * 0.035 };
    }

    return { percentage: 0, value: 0 };
  };

  const baseAmount = useMemo(() => {
    return totalPriceLocal + shippingCost;
  }, [totalPriceLocal, shippingCost]);

  const commission = useMemo(() => {
    return calculateCommission(baseAmount, selectedPaymentMethod || "");
  }, [baseAmount, selectedPaymentMethod]);

  const totalToPay = useMemo(() => {
    return (
      baseAmount +
      commission.value +
      (selectedPaymentMethod === "card_payment" ? 500 : 0)
    );
  }, [baseAmount, selectedPaymentMethod, commission.value]);

  return (
    <Card className="bg-white border-0 rounded-3 mb-4">
      <Card.Body className="p-4">
        <h4 className="fw-semibold mb-4">Resumen del pedido</h4>

        {/* Mostrar primero USDT si es CoinPayment, sino mostrar moneda local primero */}
        {showUsdtAsPrimary ? (
          <>
            <div className="d-flex justify-content-between mb-4">
              <span>Costo del envío</span>
              <span className="fw-bold fs-5">
                ${shippingCost.toFixed(2)} USDT
              </span>
            </div>
            <div className="d-flex justify-content-between mb-4">
              <span>Subtotal</span>
              <span className="fw-bold fs-5">
                ${totalPrice.toFixed(2)} USDT
              </span>
            </div>
            <div className="d-flex justify-content-between mb-4">
              <span>Total a pagar</span>
              <span className="fw-bold fs-5">
                ${(totalPrice + shippingCost).toFixed(2)} USDT
              </span>
            </div>
          </>
        ) : (
          <>
            <div className="d-flex justify-content-between mb-4">
              <span>Costo del envío</span>
              <span className="fw-bold fs-5">
                {deliveryType === "DELIVERY" && !shippingCost
                  ? "Asumido por el Cliente"
                  : `$${shippingCost.toLocaleString("es-CO")} ${currency}`}
              </span>
            </div>

            <div className="d-flex justify-content-between mb-4">
              <span>Subtotal</span>
              <span className="fw-bold fs-5">
                ${totalPriceLocal.toLocaleString("es-CO")} {currency}
              </span>
            </div>

            <div className="d-flex justify-content-between mb-4">
              <span>Servicio On Line ({commission.percentage}%)</span>
              <span className="fw-bold fs-5">
                ${commission.value.toLocaleString("es-CO")} {currency}
              </span>
            </div>

            {selectedPaymentMethod === "card_payment" && (
              <div className="d-flex justify-content-between mb-4">
                <span>Costo operación</span>
                <span className="fw-bold fs-5">$500 {currency}</span>
              </div>
            )}

            <div className="d-flex justify-content-between mb-4">
              <span>Total a pagar</span>
              <span className="fw-bold fs-5">
                ${totalToPay.toLocaleString("es-CO")} {currency}
              </span>
            </div>
          </>
        )}

        {/* Mostrar puntos de fidelidad y bono influencer */}
        <div className="mt-4 pt-3 border-top">
          <div className="d-flex justify-content-between mb-2">
            <span>Puntos</span>
            <p>{totalLoyaltyPoints.toLocaleString()}</p>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

export default OrderSummary;
